import { styled } from '@mui/material/styles';
import ContactSalesButton from '@features/Paywall/ContactSalesButton';
import SubscribeForm from '@features/SubscribeForm';
import { useSubscribeForm } from '@features/SubscribeForm/useSubscribeForm';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { SUBSCRIBE_STATE } from '@constants/billing';
import { useTranslation } from '@hooks';
import usePermissions from '@hooks/usePermissions';

const Backdrop = styled('div')({
    position: 'fixed',
    inset: 0,
    zIndex: 9998,
    overflowY: 'auto',
    background: 'linear-gradient(160deg, var(--primary-main) 0%, var(--primary-dark) 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '34px 20px',
});

const Card = styled('div')({
    backgroundColor: 'var(--light-main)',
    borderRadius: 8,
    width: '100%',
    maxWidth: 460,
    padding: '26px 30px 24px',
    textAlign: 'center',
    boxShadow: '0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12)',
    '@media (max-width: 600px)': {
        padding: 20,
    },
});

const Actions = styled('div')({
    display: 'flex',
    gap: 8,
    justifyContent: 'center',
    marginTop: 16,
    flexWrap: 'wrap',
});

const COPY = {
    [SUBSCRIBE_STATE.FREEMIUM_RETIRED]: {
        title: 'SubscribeLock.freemiumRetiredTitle',
        subtitle: 'SubscribeLock.freemiumRetiredSubtitle',
    },
    [SUBSCRIBE_STATE.TRIAL_EXPIRED]: {
        title: 'SubscribeLock.trialEndedTitle',
        subtitle: 'SubscribeLock.trialEndedSubtitle',
    },
};

const SubscribeLock = ({ subscribeState }) => {
    const { hasFullSettings } = usePermissions();
    const { t } = useTranslation();
    const form = useSubscribeForm();

    const canPay = hasFullSettings();
    const copy = COPY[subscribeState] ?? COPY[SUBSCRIBE_STATE.TRIAL_EXPIRED];

    return (
        <Backdrop>
            <Card>
                <Typography variant="h5" color="textPrimary" sx={{ marginBottom: '4px' }}>
                    {t(copy.title)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                    {t(copy.subtitle)}
                </Typography>

                <SubscribeForm form={form} />

                {canPay ? (
                    <Actions>
                        <Button variant="contained" onClick={form.onSubscribe} disabled={form.isLoading}>
                            {t('SettingsGeneral.proceedToPayment')}
                        </Button>
                        <ContactSalesButton variant="text" />
                    </Actions>
                ) : (
                    <Typography
                        variant="caption"
                        sx={{ display: 'block', marginTop: '16px', color: 'var(--text-tertiary)' }}
                    >
                        {t('SubscribeLock.askAccountOwner')}
                    </Typography>
                )}
            </Card>
        </Backdrop>
    );
};

export default SubscribeLock;
